// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// tutorial.ts — seven scripted steps on the real ship.
//
// THIS IS DATA, NOT A SECOND IMPLEMENTATION, AND THAT IS THE WHOLE POINT. Each step names what the
// player is allowed to do, a predicate over real `State` that says when it is done, and a canonical
// `solve()` — all evaluated against the SHIPPED rules in game.ts. A tutorial with its own private
// board logic is a lie the moment anybody touches the rules, and worse, it is not even a DETECTABLE
// lie: it keeps teaching the old game, confidently, forever. Because these steps are data,
// tests/tutorial.test.ts replays `solve()` through the real `apply()` and asserts each one is legal
// and reachable from the state the previous step produced.
//
// STEP 7 IS THE ONE THE WHOLE SEQUENCE EXISTS TO REACH. Steps 1-3 teach the cable and the budget,
// which is pleasant. Step 4 charges the core for the first time. Step 5 trips the bus, deliberately,
// because a rule you have only read is a rule you will break. Step 6 shows you water putting a fire
// out for free. And step 7 asks you to pump that same water back out — the moment the player finds
// out that a hazard is a tool right up until it isn't.
//
// THREE THINGS IN HERE ARE THE WAY THEY ARE BECAUSE tests/tutorial.test.ts DROVE THE REAL RULES
// THROUGH THEM AND THEY DID NOT SURVIVE. All three were fixed HERE, in the data, because in every
// case it was the lesson that was wrong and not the ship:
//
//   * STEP 3 MUST NOT REACH PORT THROUGH THE PUMP. It used to route 10>7>6>3>0>1, which lights PUMP
//     four steps before step 7 asks the player to light PUMP — so step 7, THE MOMENT, was already
//     `done` on the frame it opened and advanced itself without the player touching the board. It
//     now goes 10>7>4>3>0>1 (through the mid hold), and PUMP is off its `cells` so no route the gate
//     accepts can light it early either.
//   * STEP 6'S FIRE MUST STAY UNDER 50. `FIRE_SPREAD` throws fire into every neighbour of a
//     compartment burning at 50 or more, so the old fire of 70 — with the player told to leave it
//     alone and watch — set eight of the twelve compartments alight inside five seconds and cooked
//     the COOLANT the cable was running through to 100. "Letting a compartment flood is a real way
//     to put a fire out" is a true sentence about the shipped rules; it was not a true sentence
//     about that fire.
//   * SO THE BURNING COMPARTMENT HAS TO BE ONE YOU ARE POWERING. Under 50 the water wins in
//     fire/14 seconds, and a window short enough to be safe is too short to watch. Lit, the fire
//     grows at FIRE_GROW_POWERED and the same water wins at fire/8 — which buys a fire of 44 a
//     five-and-a-half second window that never crosses the spread line. That is why step 6 burns
//     COOLANT and not the starboard hold: COOLANT is the one compartment that is lit by every route
//     step 3 permits, is outside SUPPRESSION's radius (which would have done the killing instead of
//     the water), holds no battery to drown, and — the reason step 7 works at all — is adjacent to
//     PUMP. PUMP drains ITSELF AND ITS NEIGHBOURS, so the starboard hold at index 8 was never
//     drainable from PUMP by any route; "now drain it" was an instruction the board could not obey.
//
// The step-6 window is asserted at four seconds minimum, so a tuning pass that moves
// SUPPRESS_BY_WATER or FIRE_GROW_POWERED turns the tutorial RED instead of quietly unpassable.
//
// It runs SOLO ONLY. In a live room the local Game is stomped by a snapshot every 83ms, so a
// completion condition could never latch and the tutorial would be unfinishable — which is worse
// than not having one.

import type { Act, Game } from './game';

export interface Step {
  /** One sentence, imperative, naming the thing to touch. */
  copy: string;
  /** What the player learns the instant the step completes. */
  after: string;
  /** Compartments the player may route through or act on. Empty means "no board input". */
  cells: number[];
  /** Which verbs are live: 0 route, 1 seal, 2 douse, 3 vent, 4 force. */
  verbs: number[];
  /** Scripted damage applied to the REAL state machine when the step opens. */
  setup?: (g: Game) => void;
  /** True once the player has done the thing. Evaluated against real state. */
  done: (g: Game) => boolean;
  /** The canonical solution, replayed by tests/tutorial.test.ts through the shipped rules. */
  solve: (g: Game) => Act[];
  /** A watch step lets the clock run while the player looks. */
  watch?: boolean;
}

// Patrol's layout, named so the steps read as instructions rather than indices.
//   0 SUPPRESSION  1 HELM     2 SENSORS
//   3 cond(CELL)   4 conduit  5 conduit
//   6 PUMP         7 COOLANT  8 conduit
//   9 cond(CELL)  10 REACTOR 11 conduit
export const HELM = 1;
export const COOLANT = 7;
export const SENSORS = 2;
export const PUMP = 6;
export const SUPPRESSION = 0;
export const MID = 4;
export const PORT = 3;
export const STARBOARD = 8;

const act = (k: Act['k'], c: number | undefined, p: number[] | undefined, g: Game): Act => ({
  k,
  c,
  p,
  tk: g.tick,
  sq: 0, // the caller stamps a real sequence; tests use apply() directly with increasing numbers
});

const litAll = (g: Game, cells: number[]): boolean =>
  cells.every((c) => g.spine.slice(0, g.poweredLen).includes(c));

export const STEPS: Step[] = [
  {
    copy: 'Drag up from the REACTOR to light HELM.',
    after: 'The ship only moves while HELM has power.',
    cells: [10, COOLANT, MID, HELM],
    verbs: [0],
    done: (g) => litAll(g, [HELM]),
    solve: (g) => [act(0, undefined, [COOLANT, MID, HELM], g)],
  },
  {
    copy: 'Keep going — add SENSORS on the end.',
    after: 'Seven power, and the cable is using five. The dashed tail would do nothing.',
    cells: [10, COOLANT, MID, HELM, SENSORS],
    verbs: [0],
    done: (g) => litAll(g, [HELM, SENSORS]),
    solve: (g) => [act(0, undefined, [COOLANT, MID, HELM, SENSORS], g)],
  },
  {
    copy: 'Route through the port conduit instead — its battery is free.',
    after: 'A charged cell costs no power at all, for twelve seconds.',
    // PUMP AND THE AFT CONDUIT ARE DELIBERATELY NOT HERE. Both of them reach PORT, and both of them
    // light PUMP on the way — which hands the player step 7's answer four steps early and makes THE
    // MOMENT complete itself on the frame it opens. The mid hold is the third way in and the only
    // one that leaves the pump dark.
    cells: [10, COOLANT, MID, PORT, SUPPRESSION, HELM],
    verbs: [0],
    done: (g) => litAll(g, [PORT, HELM]),
    solve: (g) => [act(0, undefined, [COOLANT, MID, PORT, SUPPRESSION, HELM], g)],
  },
  {
    copy: 'Seal the breach amidships — tap it, then hold SEAL.',
    after: 'The heat gauge just moved. Every action you take charges the reactor.',
    cells: [MID],
    verbs: [1],
    setup: (g) => g.strike(MID, 0),
    done: (g) => g.cells[MID].breaches === 0,
    solve: (g) => [act(1, MID, undefined, g)],
  },
  {
    copy: 'Seal three more breaches — they all opened at once. Fast as you can.',
    after: 'FOUR actions inside two and a half seconds trips the bus and the whole ship goes dark. Space them out.',
    cells: [MID, PORT, STARBOARD],
    verbs: [1],
    setup: (g) => {
      g.strike(MID, 0);
      g.strike(PORT, 0);
      g.strike(STARBOARD, 0);
    },
    // `tripUntil > g.tick`, i.e. the bus is dark RIGHT NOW — not `> 0`, which is true forever after
    // the first trip of the run and would let a player who tripped the bus fiddling with the cable
    // back in step 2 walk through this step without sealing anything. And all THREE, not two: a
    // breach left open here goes on flooding through the two steps that follow, and the starboard
    // hold at 100 would cut the cable in a step whose whole instruction is "hands off".
    done: (g) =>
      g.tripUntil > g.tick ||
      (g.cells[MID].breaches === 0 &&
        g.cells[PORT].breaches === 0 &&
        g.cells[STARBOARD].breaches === 0),
    solve: (g) => [
      act(1, MID, undefined, g),
      act(1, PORT, undefined, g),
      act(1, STARBOARD, undefined, g),
    ],
  },
  {
    copy: 'Watch — COOLANT is burning and flooding. Hands off.',
    after: 'Water kills fire. Letting a compartment flood is a real way to put one out.',
    cells: [],
    verbs: [],
    watch: true,
    // 44, not 70: at 50 a fire throws FIRE_SPREAD into every neighbour every tick, and a step that
    // tells the player to stand and watch must not be the step that sets the rest of the ship on
    // fire. 35, not 42: over SUPPRESS_FLOOD_AT so the water actually fights the fire, under
    // SHORT_FLOOD_AT so a compartment the cable is running through cannot short while the player is
    // being told to do nothing. COOLANT is lit by every route step 3 allows, so this burns at
    // FIRE_GROW_POWERED against SUPPRESS_BY_WATER — 44 over 8, five and a half seconds to watch.
    setup: (g) => {
      g.cells[COOLANT].fire = 44;
      g.cells[COOLANT].flood = 35;
    },
    done: (g) => g.cells[COOLANT].fire <= 0,
    solve: () => [],
  },
  {
    copy: 'Drain it — light PUMP, one compartment past COOLANT.',
    after: 'A hazard is a tool, right up until it is not. That is the whole game.',
    // PUMP drains itself and its neighbours, and COOLANT is one of them — which is the only reason
    // this instruction is obeyable. Both routes the gate accepts (through COOLANT, or round through
    // the aft conduit) put PUMP on the prefix and the standing water in COOLANT inside its reach.
    cells: [10, COOLANT, PUMP, 9],
    verbs: [0],
    done: (g) => litAll(g, [PUMP]),
    solve: (g) => [act(0, undefined, [COOLANT, PUMP], g)],
  },
];

/**
 * What a step permits. Lives here rather than in main.ts so the test can check REFUSAL as well as
 * success — "a wrong move is refused without breaking the sequence" is half of what makes a tutorial
 * a tutorial, and it is not observable from the happy path.
 */
export function allowedIn(step: number, a: Act): boolean {
  const s = STEPS[step];
  if (!s) return false;
  if (!s.verbs.includes(a.k)) return false;
  if (a.k === 0) return (a.p ?? []).every((c) => s.cells.includes(c));
  return a.c != null && s.cells.includes(a.c);
}

/** Every scripted compartment must exist on Patrol's board. Cheap, and it has caught a typo. */
export function stepsAreOnTheBoard(cellCount: number): boolean {
  return STEPS.every((s) => s.cells.every((c) => c >= 0 && c < cellCount));
}
